import env from "../config/env";

export const resolveAvatar = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http://') || avatar.startsWith('https://') || avatar.startsWith('data:')) return avatar;
  const cleanAvatar = avatar.replace(/^uploads\//, '').replace(/^\//, '');
  return `${env.uploadsBaseUrl}/${cleanAvatar}`;
};

export const resolveUploadAsset = (asset) => {
  if (!asset) return '';
  if (asset.startsWith('http://') || asset.startsWith('https://') || asset.startsWith('data:') || asset.startsWith('/')) {
    return asset;
  }
  const cleanAsset = asset.replace(/^uploads\//, '').replace(/^\//, '');
  return `${env.uploadsBaseUrl}/${cleanAsset}`;
};

const thumbableImageExtensions = /\.(jpe?g|png|webp)$/i;

export const resolveUploadThumbnail = (asset) => {
  if (!asset || asset.startsWith('data:') || !thumbableImageExtensions.test(asset.split('?')[0])) {
    return resolveUploadAsset(asset);
  }

  const buildThumbPath = (value) => {
    const [pathPart, queryPart] = value.split('?');
    const cleanPath = pathPart.replace(/^\//, '').replace(/^uploads\//, '');
    const parts = cleanPath.split('/');
    const filename = parts.pop();
    if (!filename) return resolveUploadAsset(asset);
    const basename = filename.replace(/\.[^.]+$/, '');
    parts.push('thumbs', `${basename}.webp`);
    return `${parts.join('/')}${queryPart ? `?${queryPart}` : ''}`;
  };

  if (asset.startsWith('http://') || asset.startsWith('https://')) {
    try {
      const url = new URL(asset);
      const parts = url.pathname.split('/').filter(Boolean);
      const uploadsIndex = parts.lastIndexOf('uploads');
      if (uploadsIndex === -1) return asset;
      const filename = parts.pop();
      if (!filename) return asset;
      const basename = filename.replace(/\.[^.]+$/, '');
      const prefix = parts.slice(0, uploadsIndex + 1).join('/');
      url.pathname = `/${prefix}/thumbs/${basename}.webp`;
      return url.toString();
    } catch {
      return asset;
    }
  }

  return `${env.uploadsBaseUrl}/${buildThumbPath(asset)}`;
};

export const formatDisplayDate = (value, options = {}) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: options.short ? 'short' : 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const seededBookRating = (book = {}) => {
  const key = String(book.id || book._id || book.title || '');
  let total = 0;
  for (let i = 0; i < key.length; i += 1) {
    total += key.charCodeAt(i);
  }
  return Number((4.3 + (total % 7) / 10).toFixed(1));
};

const seededBookReviews = (book = {}) => {
  const key = String(book.id || book._id || book.title || '');
  let total = 0;
  for (let i = 0; i < key.length; i += 1) {
    total += key.charCodeAt(i) * (i + 1);
  }
  return 180 + (total % 1900);
};

export const normalizeBook = (book = {}) => {
  const id = book.id || book._id;
  const rating = Number(book.rating);
  const reviews = Number(book.reviews);

  return {
    ...book,
    id,
    cover: resolveUploadThumbnail(book.coverThumbnail || book.thumbnail || book.cover || book.coverImage),
    coverOriginal: resolveUploadAsset(book.cover || book.coverImage),
    pdf: resolveUploadAsset(book.pdf || book.fileUrl),
    readerPages: book.readerPages || book.pages || 1,
    rating: Number.isFinite(rating) && rating > 0 ? Math.min(rating, 4.9) : seededBookRating(book),
    reviews: Number.isFinite(reviews) && reviews > 0 ? reviews : seededBookReviews(book),
    printPrice: Number(book.printPrice || book.price || 0),
    ebookPrice: Number(book.ebookPrice || book.price || 0),
    language: book.language || 'English',
    hardBindingAvailable: Boolean(book.hardBindingAvailable),
    hardBindingPrice: Number(book.hardBindingPrice || 0),
    category: book.category || 'Book',
    author: book.author || 'Dolat Khan Kakar',
    isSave: Boolean(book.isSave),
    isSaved: Number(book.isSaved || book.isSave || 0),
    isReading: Number(book.isReading || 0),
  };
};

export const getSubscriptionMonths = (value) => {
  const text = String(value || '').toLowerCase();
  if (text.includes('annual') || text.includes('12')) return 12;
  if (text.includes('9')) return 9;
  if (text.includes('half') || text.includes('6')) return 6;
  if (text.includes('quarter') || text.includes('3')) return 3;
  if (text.includes('month')) return 1;
  return 0;
};

export const getSubscriptionPlansPayload = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.plans)) return payload.plans;
  if (Array.isArray(payload?.data?.plans)) return payload.data.plans;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const normalizeSubscriptionPlan = (plan = {}) => ({
  ...plan,
  id: plan.id || plan._id || '',
  price: Number(plan.price || 0),
});

export const findSubscriptionPlanForMonths = (plans = [], requiredMonths, fallbackPlan = null) => {
  const eligible = plans
    .map(normalizeSubscriptionPlan)
    .filter((plan) => plan?.isActive !== false)
    .map((plan) => ({
      ...plan,
      months: getSubscriptionMonths(`${plan.name} ${plan.period}`),
    }))
    .filter((plan) => plan.months >= requiredMonths)
    .sort((a, b) => a.months - b.months || a.price - b.price);

  return eligible[0] || fallbackPlan;
};

export const getSubscriptionCheckoutPath = (plan = {}) => {
  const safePlan = normalizeSubscriptionPlan(plan);
  const planName = `${safePlan.name || 'Subscription'} Subscription`;
  const planId = safePlan.id ? `?planId=${encodeURIComponent(safePlan.id)}` : '';
  return `/payment/sub/${encodeURIComponent(planName)}/${safePlan.price}${planId}`;
};

export const hasActiveSubscription = (user) => {
  if (!user || user.subscriptionType === 'free') return false;
  const expiry = user.subscriptionExpiryDate ? new Date(user.subscriptionExpiryDate) : null;
  return Boolean(user.isSubscribed && expiry && expiry.getTime() > Date.now());
};

export const canReadBook = (user, book = {}) => {
  if (!hasActiveSubscription(user)) return false;
  const planMonths = getSubscriptionMonths(user.subscriptionType);
  const language = String(book?.language || '').trim().toLowerCase();

  if (planMonths >= 3) return true;
  return planMonths >= 1 && language === 'english';
};

export const getRequiredReadingMonths = (book = {}) => {
  const language = String(book?.language || '').trim().toLowerCase();
  return language === 'english' ? 1 : 3;
};

export const canAccessAudiobooks = (user) =>
  hasActiveSubscription(user) && getSubscriptionMonths(user.subscriptionType) >= 6;

export const canContactAuthor = (user) => {
  return canAccessAudiobooks(user);
};

export const normalizeEvent = (event = {}) => {
  const id = event.id || event._id;
  const celebrities = Array.isArray(event.celebrities) ? event.celebrities : [];
  const celebrity = event.celebrity || celebrities[0] || {};
  const gallery = (event.gallery || event.images || []).map(resolveUploadThumbnail).filter(Boolean);
  const displayDate = formatDisplayDate(event.date);

  return {
    ...event,
    id,
    gallery,
    celebrity,
    date: displayDate,
    badgeDate: event.badgeDate || formatDisplayDate(event.date, { short: true }),
    quote: event.quote || event.eventQoute || celebrity.quote || '',
    testimonials: event.testimonials || celebrities.map((item) => ({
      name: item.name,
      role: item.title,
      avatar: item.avatar,
      quote: item.quote,
    })).filter((item) => item.name || item.quote),
  };
};

export const normalizeBlog = (blog = {}) => {
  const id = blog.id || blog._id;
  const media = (blog.media || [])
    .map((item) => ({
      ...item,
      url: resolveUploadAsset(item.url),
      type: item.type === 'video' ? 'video' : 'image',
    }))
    .filter((item) => item.url);

  return {
    ...blog,
    id,
    author: blog.author || 'Author Journal',
    media,
    cover: media[0] || null,
    date: formatDisplayDate(blog.createdAt || blog.updatedAt),
  };
};
